"use client"

import { useUbPDPState } from "@/store/ubPDPState"
import { useTranslations } from "next-intl"

import { formatPrice } from "@/lib/helper"
import { CardPrices, determineCardPrices, formatPackagingUnit } from "@/lib/price-utils"
import { PdpContext } from "@/lib/storefront/product/pdp-service"
import { usePdpDynamicContext } from "@/components/product/pdp/usePdpDynamicContext"

function ScalePriceLine(props: {
  threshold: number
  unit: string
  pricePerUnit: number
  currency: string
  isoLocale: string
}) {
  const t = useTranslations("storefront")
  const pricePerUnit = formatPrice(props.isoLocale, props.pricePerUnit, props.currency)
  // TODO correctly build singular und plural for props.unit
  return (
    <>
      <div className="flex flex-row gap-x-1 border-t border-t-gray-200 px-2 py-1 font-bold">
        <span>{t("product.price_from")}</span>
        <span>{props.threshold}</span>
        <span>{props.unit}</span>
      </div>
      <div className="border-t border-l border-t-gray-200 p-1">
        <span className="font-bold">{pricePerUnit}</span>
        <span className="px-1">{t("product.per")}</span>
        {props.unit}
      </div>
    </>
  )
}

export default function ProductDetailBuyboxScalePrice(props: {
  pdpContext: PdpContext
  showEcoTaxFlag: boolean | undefined
}) {
  const t = useTranslations("storefront")
  const { setBuyboxQuantity } = useUbPDPState()
  const pdpContext = usePdpDynamicContext(props.pdpContext)
  const cardPrices: CardPrices = determineCardPrices(
    pdpContext.isoLocale,
    pdpContext.isB2B,
    pdpContext.prices
  )
  const scalePrices = cardPrices.scalePrices
  const packagingUnitInfo = ((): string => {
    const unitInfo = formatPackagingUnit(t, pdpContext.productAndArticle.article?.packingUnit ?? "PCE")
    return unitInfo?.length ? unitInfo : t("packaging_unit.PCE")
  })()

  const handleClick = (quantity: number) => {
    setBuyboxQuantity(quantity)
  }

  return (
    !(pdpContext.pdpViewMode === "MASTER_PRODUCT") &&
    scalePrices &&
    scalePrices?.length > 0 && (
      <div className="flex flex-col text-sm">
        <div className="grid grid-cols-[repeat(2,auto)] rounded-sm border border-gray-200">
          <div className="col-span-2 px-2 py-1 font-bold">{t("product.volume_discount")}</div>
          {scalePrices.map((scalePrice, index) => (
            <button
              className="col-span-2 grid cursor-pointer grid-cols-subgrid hover:text-primary"
              key={index}
              onClick={() => handleClick(scalePrice.minQuantity)}
            >
              <ScalePriceLine
                isoLocale={pdpContext.isoLocale}
                threshold={scalePrice.minQuantity}
                pricePerUnit={scalePrice.pricePerUnit}
                unit={packagingUnitInfo ?? ""}
                currency={scalePrice.currency ?? ""}
              />
            </button>
          ))}
        </div>
      </div>
    )
  )
}
